import { CSSProperties, PointerEvent as RPointerEvent, useEffect, useRef } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { Channel, PlaylistClip } from '../core/project/types';
import { useAppStore } from '../state/store';

const STEP_W = 30;
const LABEL_W = 64;
const TOTAL_STEPS = 1024;

interface ClipDrag {
  type: 'move' | 'resize';
  clipId: string;
  startX: number;
  origStart: number;
  origLen: number;
}

/**
 * Playlist / arrangement: one lane per channel, clips reference patterns.
 * Click a lane to add a clip, drag to move, drag the right edge to resize,
 * right-click to delete. Drag on the ruler to set the loop range.
 */
export function Playlist() {
  const project = useAppStore((s) => s.project);
  const { addClip, updateClip, removeClip, setLoop } = useAppStore.getState();
  const playheadRef = useRef<HTMLDivElement>(null);
  const clipDragRef = useRef<ClipDrag | null>(null);
  const loopDragRef = useRef<null | { startStep: number }>(null);

  const stepsPerBar = project.timeSig.beats * 4;
  const bars = Math.ceil(TOTAL_STEPS / stepsPerBar);

  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const el = playheadRef.current;
      if (el) {
        const pos = engine.positionStep;
        el.style.transform = `translateX(${LABEL_W + pos * STEP_W}px)`;
        el.style.opacity = engine.isPlaying ? '1' : '0';
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Loop range (ruler drag) ──────────────────────────────────────────────
  const rulerStep = (e: RPointerEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return Math.max(0, Math.floor((e.clientX - rect.left) / STEP_W));
  };

  const onRulerDown = (e: RPointerEvent) => {
    const step = rulerStep(e);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    loopDragRef.current = { startStep: step };
    setLoop({ enabled: true, startStep: step, endStep: step + 1 });
  };

  const onRulerMove = (e: RPointerEvent) => {
    const d = loopDragRef.current;
    if (!d) return;
    const step = rulerStep(e);
    setLoop({
      enabled: true,
      startStep: Math.min(d.startStep, step),
      endStep: Math.max(d.startStep, step) + 1,
    });
  };

  const onRulerUp = () => {
    loopDragRef.current = null;
  };

  // ── Clips ────────────────────────────────────────────────────────────────
  const createClip = (e: RPointerEvent, ch: Channel) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const step = Math.floor((e.clientX - rect.left) / STEP_W);
    if (step < 0) return;
    const pattern = project.patterns[ch.patternId];
    addClip(ch.id, step, ch.patternId, pattern?.length ?? 16);
  };

  const startClipDrag = (e: RPointerEvent, clip: PlaylistClip, type: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    clipDragRef.current = {
      type,
      clipId: clip.id,
      startX: e.clientX,
      origStart: clip.startStep,
      origLen: clip.length,
    };
  };

  const onClipMove = (e: RPointerEvent) => {
    const d = clipDragRef.current;
    if (!d) return;
    const dSteps = Math.round((e.clientX - d.startX) / STEP_W);
    if (d.type === 'move') {
      updateClip(d.clipId, { startStep: Math.max(0, d.origStart + dSteps) });
    } else {
      updateClip(d.clipId, { length: Math.max(1, d.origLen + dSteps) });
    }
  };

  const endClipDrag = () => {
    clipDragRef.current = null;
  };

  return (
    <div className="pl-scroll">
      <div className="pl-grid">
        <div
          className="pl-ruler"
          onPointerDown={onRulerDown}
          onPointerMove={onRulerMove}
          onPointerUp={onRulerUp}
          title="Drag to set the loop range"
        >
          <div className="pl-lanelabel" />
          {Array.from({ length: bars }, (_, i) => (
            <div key={i} className="pl-barnum" style={{ left: (i + 1) * stepsPerBar * STEP_W }}>
              {i + 1}
            </div>
          ))}
          {project.loop.enabled && (
            <div
              className="pl-loop"
              style={{
                left: project.loop.startStep * STEP_W,
                width: (project.loop.endStep - project.loop.startStep) * STEP_W,
              }}
            />
          )}
        </div>

        {project.channels.map((ch) => (
          <div key={ch.id} className="pl-lane" style={{ '--ch-color': ch.color } as CSSProperties}>
            <div className="pl-lanelabel">{ch.name}</div>
            <div className="pl-lanebody" onPointerDown={(e) => createClip(e, ch)}>
              {project.playlist
                .filter((c) => c.channelId === ch.id)
                .map((clip) => (
                  <div
                    key={clip.id}
                    className="pl-clip"
                    style={{ left: clip.startStep * STEP_W, width: clip.length * STEP_W - 1 }}
                    onPointerDown={(e) => startClipDrag(e, clip, 'move')}
                    onPointerMove={onClipMove}
                    onPointerUp={endClipDrag}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeClip(clip.id);
                    }}
                    title={`${project.patterns[clip.patternId]?.name ?? 'clip'} — drag to move, right edge to resize, right-click to delete`}
                  >
                    <span className="pl-clip-label">
                      {project.patterns[clip.patternId]?.name ?? 'clip'}
                    </span>
                    <div
                      className="pl-clip-grip"
                      onPointerDown={(e) => startClipDrag(e, clip, 'resize')}
                      onPointerMove={onClipMove}
                      onPointerUp={endClipDrag}
                    />
                  </div>
                ))}
            </div>
          </div>
        ))}

        <div className="pl-playhead" ref={playheadRef} />
      </div>
    </div>
  );
}